import { useState, useRef, useEffect, useCallback } from 'react';
import useSearchParams from './useSearchParams';

const DEBOUNCE_DELAY = 500;

/**
 * Debounced search-param input hook.
 * Returns local state + a setter that debounces the URL search param update.
 * Also provides a `clear` helper that cancels any pending debounce and removes the param.
 */
export function useDebouncedSearchParam(paramKey: string, currentValue: string) {
  const { setSearchParam, removeSearchParamSingle } = useSearchParams();
  const [localValue, setLocalValue] = useState(currentValue);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Sync local state when URL param changes externally
  useEffect(() => {
    setLocalValue(currentValue);
  }, [currentValue]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const debouncedSet = useCallback(
    (value: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        if (value) {
          setSearchParam(paramKey, value);
        } else {
          removeSearchParamSingle(paramKey);
        }
      }, DEBOUNCE_DELAY);
    },
    [paramKey, setSearchParam, removeSearchParamSingle]
  );

  const clear = useCallback(() => {
    setLocalValue('');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    removeSearchParamSingle(paramKey);
  }, [paramKey, removeSearchParamSingle]);

  return { localValue, setLocalValue, debouncedSet, clear };
}
